import Axios, { AxiosError, CancelTokenSource } from 'axios';
import React from 'react';
import { FormApi, FORM_ERROR } from 'final-form';
import AxiosHelper, { CustomAxiosRequestConfig } from 'services/AxiosHelper';
import {
	EditContextState,
	EntityVersion,
	EntityVersionType,
	FieldSuggestionMap,
	SubmissionState,
	EntityDefinition,
	FormValuesTransformHandler,
	QueryResponse,
} from 'types';
import { getLastLocation } from '../LastLocationProvider';
import EditContext from './EditContext';
import { handleAxiosErrorWithValidation, getValidationErrorSource, ValidationErrorSource } from 'util/Misc';
import { getTooltipsForEntity } from 'services/EntityDefinitionService';
import { SyncCustomFrontEndValidation } from './fields/mutators/mutators';
import { toastSuccess } from '../toast/Toasts';
import { toast } from 'react-toastify';
import { getHandleableException } from 'types/Exceptions';
import AppState from 'AppState';
import { Redirect } from 'react-router';
import moment, { Moment } from 'moment';
import RecordScope from 'types/__models__/RecordScope';
import { ModelStatus } from '../../../../../Roc.SharedClientApp/types/model-status';
import BaseDto from 'types/__models__/BaseDto';
import { GetCurrentLocationWithoutPrefix } from 'services/LocationHelper';

type FormValuesTransformMap = { [key: string]: FormValuesTransformHandler };

/**
 * Higher-Order Component (HOC) to have reusable functions to handle edit page features
 * This higher order component builds parts of the EditContextState around the wrapped component
 *
 * *** React convention for passing down props:
 * https://reactjs.org/docs/higher-order-components.html#convention-pass-unrelated-props-through-to-the-wrapped-component
 * @param WrappedComponent The component being wrapped
 * @param entityDefinition The definition of the entity used by this edit page component.
 * @param isEmbeddedEdit Unknown. Please document me!
 * @template TDto The dto object representing the entity being edited or created
 * @template TQueryResponse The dto response object sent from the backend
 */
function withEdit<TDto = any, TQueryResponse extends QueryResponse<TDto> = QueryResponse<TDto>>(
	WrappedComponent: any,
	entityDefinition: EntityDefinition,
	isEmbeddedEdit = false,
) {
	class WithEdit extends React.Component<any, EditContextState<TDto, TQueryResponse>> {
		private static readonly HistoryIdParam: string = 'historyId';

		private requestToken: CancelTokenSource;
		private tooltipRequestToken: CancelTokenSource;
		public static displayName: string;
		// isMounted variable used to help prevent tests from failing due to missing 'createEvent' property
		private isMounted = false;

		/**
		 * Stores handlers that allow transforming the form data before sending it to the server.
		 */
		private formValuesTransformHandlers: FormValuesTransformMap = {};

		constructor(props) {
			super(props);

			this.state = this.getInitialState();

			this.requestToken = Axios.CancelToken.source();
			this.tooltipRequestToken = Axios.CancelToken.source();
		}

		private getInitialState(): EditContextState<TDto, TQueryResponse> {
			// the props.match comes from React Router and this is how we're getting the id
			// https://reacttraining.com/react-router/web/guides/philosophy
			const editingId = this.props.match?.params.id;

			let cultureId: string | undefined;
			if (entityDefinition.localizeMode) {
				const fallbackCulture = AppState.cultures.find((culture) => culture.isFallback);
				if (fallbackCulture) {
					cultureId = fallbackCulture.id;
				}
			}

			return {
				redirectPathName: '',
				redirectSearch: '',
				axiosError: null,
				errorOnSave: false,
				isLoadingInitial: editingId !== undefined,
				isLoading: editingId !== undefined,
				entityDefinition,
				values: undefined,
				primaryCulture: cultureId,
				currentCulture: cultureId,
				isSaving: false,
				validationError: false,
				isCreateMode: editingId === undefined,
				editingId,
				response: undefined,
				editSessionUniqueKey: '',
				// when the edit form is initialized, save off the last location so that we can return to it (and keep
				// query string parameters) when cancelling, saving, deleting, etc.
				lastLocation: getLastLocation(),
				nextSubmissionType: null,
				nextSubmissionContinues: false,
				nextSubmissionTime: null,
				force: false,
				hasTooltipError: false,
				tooltipData: {},
				entityTypeId: '',
				fieldSuggestions: {},
				scopeFieldEnabled: entityDefinition.scoped,
				redirectTo404: false,
				scopeChanged: false,
				validationErrorSource: ValidationErrorSource.None,
			};
		}

		/**
		 * Resets a component's EditContext to its initial state.
		 */
		public reset = () => {
			this.setState({
				...this.getInitialState(),
			});
		};

		/**
		 * If there's an ID in the URL, we're editing an item, so load the values
		 * for this item as soon as the component is mounted.
		 */
		public async componentDidMount(): Promise<void> {
			this.isMounted = true;
			await this.load();
		}

		/**
		 * Handles Component Updates. Primarily to make sure we reload the record we're currently modifying
		 * Whenever the location changes, which is defined in props.
		 * @param prevProps
		 */
		public componentDidUpdate(prevProps) {
			const currentPathAndQuery = this.props.location
				? this.props.location.pathname + this.props.location.search
				: null;
			const prevPathAndQuery = prevProps.location
				? prevProps.location.pathname + prevProps.location.search
				: null;

			const { redirectPathName, redirectSearch } = this.state;

			const redirectPathAndQuery = redirectPathName + '?' + redirectSearch;

			// if we switch to another URL, let's reload. this could happen because we no longer need historyId, or
			// because it got added to the query string after saving.
			const pathAndQueryChanged = currentPathAndQuery !== prevPathAndQuery;
			const redirectingToCurrentPage = redirectPathAndQuery === currentPathAndQuery;

			if (pathAndQueryChanged || redirectingToCurrentPage) {
				console.debug('Query change detected, reloading record.');

				// we make sure to clear any state that might lead us to be redirected into a blank page.
				// eslint-disable-next-line
				this.setState(this.getInitialState());

				// then we trigger a reload to make sure we have fresh data.
				this.load();
			}
		}

		/**
		 * Loads the entity being edited based on the current URL. This is called on load but can also
		 * be called from other components via editContext. That method is primarily used by the versioning
		 * dropdown but can be used by anything.
		 */
		public load = async () => {
			const id = this.props.match?.params.id;
			const singularName = this.state.entityDefinition.singularName;

			if (!id) {
				// load tooltip data
				return await getTooltipsForEntity(this.state.entityDefinition, this.tooltipRequestToken, () => {
					this.setState({
						hasTooltipError: true,
					});
				}).then((response) => {
					console.log(`Loaded tooltips for ${singularName}:`, response.data);

					this.setState({
						tooltipData: response.data.tooltipData,
						entityTypeId: response.data.entityTypeId,
					});
				});
			}

			let url = `${this.state.entityDefinition.baseAjaxRoute}/${id}`;

			const activeVersion = this.getActiveVersionId();

			if (activeVersion) {
				url += '?historyId=' + activeVersion;
			}

			try {
				this.setState({
					isLoading: true,
				});

				const loadTooltipDataPromise = getTooltipsForEntity(
					this.state.entityDefinition,
					this.tooltipRequestToken,
					() => {
						this.setState({
							hasTooltipError: true,
						});
					},
				);
				const loadPromise = AxiosHelper.get(url, {
					cancelToken: this.requestToken.token,
					redirectToAccessDeniedOn403: true,
				} as CustomAxiosRequestConfig);

				await Promise.all([loadTooltipDataPromise, loadPromise]).then((responses) => {
					// not much point in giving this a type, as this could be any type in the system.
					const [tooltipResponse, response] = responses;

					console.log(`Loaded ${singularName}:`, response.data);
					console.log(`Loaded tooltips for ${singularName}:`, tooltipResponse?.data);

					/* sometimes it's handy to pass values on the wrapper for the entity
							without having to touch the values that affect the form. for example,
							for admin users we set isLdapUser = true, but we don't want to send this
							down to be modifiable or visible, so we can send it in the wrapper as
							meta data which we store in `response`.
					*/
					// eslint-disable-next-line no-unused-vars
					const { entity, tooltipData, entityTypeId, ...rest } = response.data;

					let cultureId: string | undefined;
					cultureId = undefined;
					if (entityDefinition.localizeMode) {
						cultureId = entity.__CultureId as string;
					}

					this.setState({
						values: entity,
						currentCulture: this.state.redirectCulture || cultureId,
						primaryCulture: cultureId,
						response: rest,
						editSessionUniqueKey: url,
						tooltipData: tooltipResponse?.data?.tooltipData,
						entityTypeId: tooltipResponse?.data?.entityTypeId,
						currentScope: entity && entity.scope,
					});
				});
			} catch (error) {
				if (Axios.isCancel(error)) {
					return;
				}

				const axiosError = error as AxiosError;
				console.error(`An error occurred while loading ${singularName}: ${axiosError.message}`);

				if (axiosError && axiosError.response && axiosError.response.status === 404) {
					this.setState(() => {
						return {
							isLoading: false,
							isLoadingInitial: false,
							axiosError,
							redirectTo404: true,
						};
					});
				} else {
					this.setState({
						isLoading: false,
						isLoadingInitial: false,
						axiosError,
					});
				}

				return;
			}

			this.setState({
				isLoading: false,
				isLoadingInitial: false,
			});
		};

		private onSaveCallbacks = {};

		public registerOnBeforeSaveCallback = (name: string, cb: () => void) => {
			console.debug('Registering On Save Callback:', name);
			this.onSaveCallbacks[name] = cb;
		};

		public componentWillUnmount() {
			this.isMounted = false;
			this.requestToken.cancel();
			this.tooltipRequestToken.cancel();
		}

		private callPostSaveCallbacks = () => {
			// we need to wait for react-final-form to finish updating state before this callback
			// there is no callback from rff, so this'll have to do.
			setTimeout(() => {
				Object.keys(this.onSaveCallbacks).forEach((onSaveCallbackKey) => {
					console.debug('Post-save callback:', onSaveCallbackKey);
					this.onSaveCallbacks[onSaveCallbackKey]();
				});
			});
		};

		/**
		 * Registers a new form values transform handler
		 */
		private registerFormValuesTransformHandler = (key: string, handler: FormValuesTransformHandler) => {
			if (!key) {
				console.error('A valid key is required to register a form values transform handler.');
				return;
			}

			if (!this.formValuesTransformHandlers[key]) {
				console.debug('Form values transform handler registered:', key);
			} else {
				console.debug('Form values transform handler replaced:', key);
			}

			this.formValuesTransformHandlers[key] = handler;
		};

		/**
		 * Returns a promise that will save the form values on the server. The promise is required for
		 * react-final-form to properly handle form state if something goes wrong. For example, if there's
		 * validation, or a server error.
		 */
		public save = async (formValues: any, formApi?: FormApi): Promise<void | Record<string, unknown>> => {
			const parentId = this.props.match?.params.parentId;
			let values = { ...formValues, parentId };
			console.log(`Saving ${this.state.entityDefinition.singularName}`, values);

			if (entityDefinition.localizeMode) {
				values.__CultureId = this.state.primaryCulture;
			}

			for (const [key, handler] of Object.entries(this.formValuesTransformHandlers)) {
				console.debug(`Calling transform handler:`, key);
				values = await handler(values);
			}

			if (formApi != null) {
				// check synchronous custom validation first
				const syncValidationState = Object.keys(formValues).reduce((res, key) => {
					const fieldState = formApi.getFieldState(key);

					if (fieldState && fieldState.data) {
						// we're only interested in fields that are validation related
						const errorFields = fieldState.data as SyncCustomFrontEndValidation;

						const errors = Object.keys(errorFields).reduce((result, element) => {
							result = result.concat(errorFields[element]);
							return result;
						}, []);

						if (errors.length > 0) {
							res[key] = errors;
						}
					}

					return res;
				}, {});

				if (Object.keys(syncValidationState).length > 0) {
					// Scroll to top so users can see error messages
					window.scrollTo(0, 0);

					return { ...syncValidationState, [FORM_ERROR]: 'An error occurred while saving.' };
				}
			}

			let url = this.state.entityDefinition.baseAjaxRoute;
			const id = this.props.match?.params.id;

			if (id) {
				url += '/' + id;
			}

			if (parentId) {
				url += '?parentId=' + parentId;
			}

			const doContinue = this.state.nextSubmissionContinues;
			const submissionType = this.state.nextSubmissionType;
			let submissionTime = this.state.nextSubmissionTime;
			if (submissionTime && !moment.isMoment(submissionTime)) {
				submissionTime = moment(submissionTime);
			}
			const force = this.state.force;

			this.setState({
				isSaving: true,
				// clear the "next" submission state so that subsequent attempts to save don't do the wrong thing
				nextSubmissionContinues: false,
				nextSubmissionTime: null,
				nextSubmissionType: null,
				force: false,
				errorOnSave: false,
				validationError: false,
			});

			let newValues = { ...values };

			if (submissionType) {
				// override the status with the submission type, if specified. other types of submissions can
				// override this by calling `setNextSubmissionState` on the edit context.
				newValues = { ...newValues, status: submissionType };

				console.debug('This save is setting model status:', submissionType);
			}

			const activeVersion = this.getActiveVersion();
			if (activeVersion) {
				// if we're editing a non-current version, provide the historyId to the backend as well
				newValues = { ...newValues, historyId: activeVersion.id };
				console.debug('This save is operating with history:', activeVersion);
			}

			let response: any;
			try {
				// Set up headers
				let headers = {};
				if (submissionTime) {
					headers = { 'Roc-ScheduledTime': submissionTime.toJSON() };
					console.debug('This save is scheduled for:', submissionTime);
				}

				if (force) {
					headers = { ...headers, 'Roc-Force': true };
				}
				this.callPostSaveCallbacks();

				console.debug('Entity being sent for saving:', newValues);
				response = await AxiosHelper.post(url, newValues, {
					headers,
					cancelToken: this.requestToken.token,
					redirectToAccessDeniedOn403: true,
				} as CustomAxiosRequestConfig);

				if (!response) {
					throw new Error('Response was null');
				}
			} catch (error) {
				if (Axios.isCancel(error) || !this.isMounted) {
					// if the component was unmounted, there's nothing to do
					return;
				}

				// Check if this error can be handled by the client. If it can, rethrow it and let the wrapped component handle it.
				if (getHandleableException(error)) {
					console.debug('Found handleable exception. Rethrowing...');
					this.setState({
						isSaving: false,
						errorOnSave: false,
					});
					throw error;
				}

				const axiosError = error as AxiosError;

				console.log(`Unable to save ${this.state.entityDefinition.singularName}...`);

				// Wrap in a try-catch, to help prevent tests from failing due to missing 'createEvent' property
				try {
					this.setState({
						isSaving: false,
						errorOnSave: false,
						axiosError: null,
					});

					if (!isEmbeddedEdit) {
						// Scroll to top so users can see error messages
						window.scrollTo(0, 0);
					}

					// validation error & generic error
					const formValidationState = handleAxiosErrorWithValidation(
						axiosError,
						'An error occurred while saving.',
						() => {
							this.setState({
								validationError: true,
							});
						},
						() => {
							this.setState({
								errorOnSave: true,
								axiosError,
							});
						},
					);

					this.setState({
						validationErrorSource: getValidationErrorSource(formValidationState, axiosError),
					});

					return formValidationState;
				} catch {
					console.error('Error trying to set state, if this is not happening in a test, please investigate!');
				}
			}

			if (!isEmbeddedEdit) {
				// Display a toast so users know the save was successful
				toastSuccess({
					toastifyOptions: {
						position: toast.POSITION.BOTTOM_RIGHT,
					},
					customContent: (
						<>
							{submissionTime ? (
								<>
									{submissionType === 'Published' ? (
										<>
											{this.state.entityDefinition.singularName} successfully scheduled to be
											published on {submissionTime.format('MMMM Do, YYYY')} at{' '}
											{submissionTime.format('h:mm a')}
										</>
									) : submissionType === 'Inactive' ? (
										<>
											{this.state.entityDefinition.singularName} successfully scheduled to be set
											to inactive on {submissionTime.format('MMMM Do, YYYY')} at{' '}
											{submissionTime.format('h:mm a')}
										</>
									) : (
										// When the scheduled time was not updated, but only some entity data for future version.
										// This happend after click Update button, the submissionType is null.
										<>{this.state.entityDefinition.singularName} successfully saved.</>
									)}
								</>
							) : (
								<>
									{submissionType === 'Published' ? (
										<>{this.state.entityDefinition.singularName} successfully published.</>
									) : submissionType === 'Inactive' ? (
										<>{this.state.entityDefinition.singularName} successfully set to inactive.</>
									) : submissionType === 'Draft' ? (
										<>{this.state.entityDefinition.singularName} successfully saved as draft.</>
									) : submissionType === 'Archived' ? (
										<>{this.state.entityDefinition.singularName} successfully archived.</>
									) : (
										<>{this.state.entityDefinition.singularName} successfully saved.</>
									)}
								</>
							)}
						</>
					),
				});
			}

			if (doContinue) {
				const { entity } = response.data;

				const path = `${this.state.entityDefinition.baseEditRoute}/${entity.id}`;
				let search = '';
				if (activeVersion && this.state.continueWithHistory) {
					search = 'historyId=' + activeVersion.id;
				}

				if (this.state.entityDefinition.isBulkEdit) {
					// If we are in bulk edit mode, use the response from the POST request as the new data
					let cultureId: string | undefined;
					cultureId = undefined;
					if (entityDefinition.localizeMode) {
						cultureId = entity.__CultureId as string;
					}

					this.setState({
						values: entity,
						currentCulture: this.state.redirectCulture || cultureId,
						primaryCulture: cultureId,
						response: response,
						currentScope: entity && entity.scope ? entity.scope.value : undefined,
						isSaving: false,
						isLoading: false,
						isLoadingInitial: false,
						isCreateMode: false,
					});
				} else if (this.state.isCreateMode) {
					if (!isEmbeddedEdit) {
						// if we're continuing after creating a new entity, we need to redirect to the new record
						this.setState({
							redirectPathName: path,
							redirectSearch: search,
						});
					}
				} else {
					// we're continuing, but after editing an entity that already exists

					if (
						activeVersion &&
						activeVersion.type === EntityVersionType.Archived &&
						submissionType === ModelStatus.Published &&
						!isEmbeddedEdit
					) {
						// when restoring an archived version, drop the `historyId` param from the route
						this.props.history.replace({ search: '' });
					}

					// reload entity information in order to pull down the latest version info
					await this.load();

					const currentLocationInfo = GetCurrentLocationWithoutPrefix();
					const currentPathWithQuery = currentLocationInfo.pathname + currentLocationInfo.search;

					this.setState({
						isSaving: false,
					});

					if (path !== currentPathWithQuery && !isEmbeddedEdit) {
						this.setState({
							redirectPathName: path,
							redirectSearch: search,
						});
					}
				}
			}
			// If the current active version type is archived, then we need to set the redirect path, as it was changed when we selected this version from version modal
			else if (this.getActiveVersion()?.type === EntityVersionType.Archived) {
				const path = this.state.entityDefinition.baseRoute;
				this.setState({
					redirectPathName: path,
				});
				this.redirectToLastLocation();
			} else {
				if (!isEmbeddedEdit) {
					// not continuing, so we want to redirect back to our last location
					this.redirectToLastLocation();
				}
			}

			return response.data;
		};

		/**
		 * Redirects the browser back to the last location before the active edit form was mounted. This
		 * is often used to redirect back to the listing page (and retain any active filters).
		 */
		public redirectToLastLocation = () => {
			console.debug('Redirecting to last location...');
			let redirectPathName = '';
			let redirectSearch = '';
			const { lastLocation, entityDefinition } = this.state;

			if (lastLocation) {
				redirectPathName = lastLocation.pathname;
				redirectSearch = lastLocation.search;
			} else {
				redirectPathName = entityDefinition.baseRoute;
				redirectSearch = '';
			}

			this.setState({
				redirectPathName,
				redirectSearch,
			});
		};

		/**
		 * Informs the edit context of the kind of save that should be done when the edit form is submitted. This is
		 * used for flagging certain saves such as saving as draft, inactive, etc.
		 */
		public setNextSubmissionState = ({
			submissionType,
			submissionTime,
			doContinue,
			force,
		}: SubmissionState): Promise<void> => {
			console.debug(
				'setting next submission state to type:',
				submissionType,
				'time:',
				submissionTime,
				'continues: ',
				doContinue,
				'forced: ',
				force || false,
			);

			return new Promise((resolve) => {
				this.setState(
					{
						nextSubmissionType: submissionType,
						nextSubmissionTime: submissionTime,
						nextSubmissionContinues: doContinue,
						force: force || false,
					},
					resolve,
				);
			});
		};

		/**
		 * Retrieves a full list of the version history within the edit context.
		 */
		public getAllVersions = (): EntityVersion[] => {
			const values = this.state.values;

			const response = this.state.response;

			const previousVersions = response?.previousVersions ?? [];
			let pendingVersions = response?.pendingVersions ?? [];
			let scheduledVersions = response?.scheduledVersions ?? [];
			const currentVersionStatus = response?.currentVersionStatus;

			// This is unsound! This is only valid if `TDto` derives from BaseDto (which it should but isn't guaranteed by typing currently!)
			const currVersionNo = (values as Partial<BaseDto>)?.versionNo ?? 0;
			let nextVersionNo = currVersionNo;

			// note: pending versions need to be transformed first (before scheduled versions) as they have incrementing
			// version numbers that should be before the scheduled versions
			pendingVersions = [
				...pendingVersions.map((ver) => ({
					...ver,
					// create a fake version number for draft versions, starting from the current version's number
					versionNo: ++nextVersionNo,
					type: EntityVersionType.Draft,
				})),
			];

			scheduledVersions = [
				...scheduledVersions.map((ver) => ({
					...ver,
					// scheduled versions should then follow the draft versions
					versionNo: ++nextVersionNo,
					type: EntityVersionType.Future,
				})),
			];

			const now = moment.utc();

			return [
				// include future versions that need to be published
				...scheduledVersions,

				// and the draft versions
				...pendingVersions,

				// include a "fake" current version, so that we can switch back to editing the current version in the
				// version selector
				{
					id: null,
					createDate: ((values as Partial<BaseDto>)?.createDate as unknown as Moment | undefined) ?? now, // Type error!!! Moments aren't dates!
					modifyDate: ((values as Partial<BaseDto>)?.modifyDate as unknown as Moment | undefined) ?? now, // Type error!!! Moments aren't dates!
					createUser: (values as Partial<BaseDto>)?.createUser, // Type error. It isn't enforced that values is a BaseDto?
					modifyUser: (values as Partial<BaseDto>)?.modifyUser, // Type error. It isn't enforced that values is a BaseDto?
					versionNo: currVersionNo,
					type: EntityVersionType.Current,
					status: currentVersionStatus,
				},

				...previousVersions.map((ver) => ({ ...ver, type: EntityVersionType.Archived })),
			].sort((a, b) => b.versionNo - a.versionNo); // sort all versions descending by version number
		};

		/**
		 * Returns the unique identifier (from the query string) of the current active version.
		 * This may not necessarily be a version that exists.
		 */
		public getActiveVersionId = (): string | null => {
			const queryString = new URLSearchParams(location.search || '');

			if (queryString && queryString.get(WithEdit.HistoryIdParam)) {
				return queryString.get(WithEdit.HistoryIdParam) || null;
			}
			return null;
		};

		/**
		 * Gets the current active version within the edit context, or `null` if the current
		 * version is the latest version.
		 */
		public getActiveVersion = (): EntityVersion | null => {
			const actVersion = this.getActiveVersionId();

			if (actVersion === null) {
				return null;
			}

			return this.getVersionById(actVersion);
		};

		/**
		 * Retrieves the `EntityVersion` from the history of the edit context by id, or `null` if
		 * the version could not be found.
		 */
		public getVersionById = (id?: string): EntityVersion | null => {
			const allVersions = this.getAllVersions();

			if (!id) {
				// we're looking for the fake current version
				return allVersions.find((ver) => ver.id === null) || null;
			}

			// otherwise, search specifically by id
			const foundVersion = allVersions.find((ver) => ver.id === id);

			if (!foundVersion) {
				return null;
			}

			return foundVersion;
		};

		/**
		 * Loads the cahced tooltip data into the editContext
		 */
		private getTooltipsForEntity = () => {
			const singularName = this.state.entityDefinition.singularName;

			getTooltipsForEntity(this.state.entityDefinition, this.tooltipRequestToken, () => {
				this.setState({
					hasTooltipError: true,
				});
			}).then((response) => {
				console.log(`Loaded tooltips for ${singularName}:`, response.data);

				this.setState({
					tooltipData: response.data.tooltipData,
					entityTypeId: response.data.entityTypeId,
				});
			});
		};

		/**
		 * Update field suggestions with new values. This will add on to existing field suggestions only, and will not clear pending ones.
		 *
		 * @private
		 * @memberof WithEdit
		 */
		private setFieldSuggestions = (fieldSuggestionMap: FieldSuggestionMap) => {
			this.setState((prevState) => ({
				fieldSuggestions: { ...prevState.fieldSuggestions, ...fieldSuggestionMap },
			}));
		};

		/**
		 * We do not want to update the Primary Culture if we're editing an item. This is set during creation and we do not support
		 * updating it currently.
		 */
		private setCulture = (culture: string) => {
			this.setState({
				currentCulture: culture,
			});

			if (this.state.isCreateMode) {
				this.setState({
					primaryCulture: culture,
				});
			}
		};

		/**
		 * Sets the continueWithHistory value. When true, the current historyId in the querystring will be maintained after
		 * the Save & Continue operation.
		 */
		private setContinueWithHistory = (continueWithHistory: boolean) => {
			this.setState({
				continueWithHistory: continueWithHistory,
			});
		};

		/**
		 * Set the culture that should be set when the form reloads.
		 */
		private setRedirectCulture = (culture?: string) => {
			this.setState({
				redirectCulture: culture,
			});
		};

		/**
		 * Allows edit forms to toggle whether the scope field is enabled/disabled
		 */
		private setScopeFieldEnabled = (display: boolean) => {
			this.setState({
				scopeFieldEnabled: display,
			});
		};

		/**
		 * Set scope changed
		 */
		private setScopeChanged = (scopeChanged: boolean) => {
			this.setState({
				scopeChanged,
			});
		};

		/**
		 * Set current scope
		 */
		private setCurrentScope = (scope: RecordScope) => {
			this.setState({
				currentScope: scope,
			});
		};

		/**
		 * Set original scope
		 */
		private setOriginalScope = (scope: RecordScope) => {
			this.setState({
				originalScope: scope,
			});
		};

		public render() {
			if (this.state.redirectTo404) {
				return <Redirect to="/404" />;
			}

			return (
				// This part guarantees that the current edit context will be updated with the state of this HOC
				<EditContext.Provider
					value={{
						...this.state, // we "copy" the current context values so we can set the save function
						reset: this.reset,

						setNextSubmissionState: this.setNextSubmissionState,
						save: this.save, // we set the save function in the context so the consumers have someone to call back
						load: this.load,
						registerOnBeforeSaveCallback: this.registerOnBeforeSaveCallback,

						getAllVersions: this.getAllVersions,
						getActiveVersionId: this.getActiveVersionId,
						getActiveVersion: this.getActiveVersion,
						getVersionById: this.getVersionById,
						getTooltipsForEntity: this.getTooltipsForEntity,
						setFieldSuggestions: this.setFieldSuggestions,
						redirectToLastLocation: this.redirectToLastLocation,
						setContinueWithHistory: this.setContinueWithHistory,
						setCulture: this.setCulture,
						setScopeFieldEnabled: this.setScopeFieldEnabled,
						setRedirectCulture: this.setRedirectCulture,
						setCurrentScope: this.setCurrentScope,
						setOriginalScope: this.setOriginalScope,
						setScopeChanged: this.setScopeChanged,

						registerFormValuesTransformHandler: this.registerFormValuesTransformHandler,
					}}
				>
					{/* here we are passing down the props coming from higher level component, see link on top*** */}
					<WrappedComponent {...this.props} />
				</EditContext.Provider>
			);
		}
	}

	const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

	// convention for react dev tools.
	WithEdit.displayName = `withEdit(${wrappedComponentName})`;

	return WithEdit;
}

export default withEdit;
